"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import type { DocumentProps } from "@react-pdf/renderer";
import type { ReactElement, ReactNode } from "react";

import {
  defaultResumeTemplateId,
  resumeTemplateMap,
  resumeTemplates,
  type ResumeTemplateId,
} from "@/lib/cv/template";
import { SAMPLE_RESUME } from "@/components/cv-builder/sample-resume";
import { PdfJsPreview } from "@/components/cv-builder/pdf-preview";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { saveTemplateFile } from "@/components/landing/ats-template-download";

// Hardcoded ID copy: this gallery lives on a marketing page with no I18nProvider,
// so it cannot reuse DocxResumePreview (which depends on useI18n).
const DEFAULT_COPY = {
  title: "3 Template CV ATS Friendly (Gratis, Format .docx)",
  description:
    "Pilih template, lihat preview-nya, lalu download. Semua single-column, heading standar, dan terbaca parser ATS. Isi datanya sendiri di Word atau Google Docs.",
  downloadDocx: "Download .docx",
  downloadPdf: "Download PDF",
  loading: "Menyiapkan preview...",
  error: "Gagal memuat preview. Coba template lain atau refresh halaman.",
} as const;

type AtsTemplateGalleryCopy = Partial<typeof DEFAULT_COPY>;

// ID labels for each template id (defined in src/lib/cv/template).
const TEMPLATE_LABELS: Record<ResumeTemplateId, { name: string; description: string }> = {
  "template-1": {
    name: "Aurora Prime",
    description: "Single-column klasik, heading serif. Paling aman untuk ATS.",
  },
  "template-2": {
    name: "Borealis Flow",
    description: "Layout timeline rapi dengan aksen biru. Tetap satu kolom.",
  },
  "template-3": {
    name: "Corona Grid",
    description: "Gaya editorial dengan divider tegas. Bersih dan terbaca mesin.",
  },
};

type DocxCache = Partial<Record<ResumeTemplateId, Blob>>;
type PdfDataCache = Partial<Record<ResumeTemplateId, Uint8Array>>;

type AtsTemplateGalleryProps = {
  copy?: AtsTemplateGalleryCopy;
  showDownloadButton?: boolean;
  templateIds?: ResumeTemplateId[];
  /** Shown once a template has been downloaded: the page's next step. */
  afterDownload?: ReactNode;
};

export function AtsTemplateGallery({
  copy,
  showDownloadButton = true,
  templateIds,
  afterDownload,
}: AtsTemplateGalleryProps = {}) {
  const copyTitle = copy?.title ?? DEFAULT_COPY.title;
  const copyDescription = copy?.description ?? DEFAULT_COPY.description;
  const copyDownloadDocx = copy?.downloadDocx ?? DEFAULT_COPY.downloadDocx;
  const copyDownloadPdf = copy?.downloadPdf ?? DEFAULT_COPY.downloadPdf;
  const copyLoading = copy?.loading ?? DEFAULT_COPY.loading;
  const copyError = copy?.error ?? DEFAULT_COPY.error;
  const availableTemplates = useMemo(() => {
    if (!templateIds?.length) {
      return resumeTemplates;
    }

    return resumeTemplates.filter((template) => templateIds.includes(template.id));
  }, [templateIds]);
  const [activeId, setActiveId] = useState<ResumeTemplateId>(resumeTemplates[0].id);

  // DOCX blobs are built lazily for download only; the preview comes from the
  // same @react-pdf/renderer templates the review flow previews.
  const docxCacheRef = useRef<DocxCache>({});
  const [pdfDataCache, setPdfDataCache] = useState<PdfDataCache>({});
  const [isBuilding, setIsBuilding] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFormat, setDownloadingFormat] = useState<
    "docx" | "pdf" | null
  >(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  useEffect(() => {
    if (availableTemplates.some((template) => template.id === activeId)) {
      return;
    }

    setActiveId(availableTemplates[0]?.id ?? defaultResumeTemplateId);
  }, [activeId, availableTemplates]);

  // Build the PDF preview for the active template.
  useEffect(() => {
    if (pdfDataCache[activeId]) {
      setIsBuilding(false);
      return;
    }

    let cancelled = false;
    setIsBuilding(true);
    setError(null);

    const buildPdf = async () => {
      try {
        const template = resumeTemplateMap[activeId];
        const element = template.renderPdf(SAMPLE_RESUME);
        const { pdf } = await import("@react-pdf/renderer");
        const blob = await pdf(
          element as ReactElement<DocumentProps>,
        ).toBlob();
        if (cancelled) return;
        const data = new Uint8Array(await blob.arrayBuffer());
        setPdfDataCache((previous) => ({ ...previous, [activeId]: data }));
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to build template PDF preview", err);
        setError(copyError);
      } finally {
        if (!cancelled) setIsBuilding(false);
      }
    };

    void buildPdf();

    return () => {
      cancelled = true;
    };
  }, [activeId, pdfDataCache, copyError]);

  const ensureDocxBlob = useCallback(async (templateId: ResumeTemplateId) => {
    const cached = docxCacheRef.current[templateId];
    if (cached) return cached;

    const template = resumeTemplateMap[templateId];
    const document = template.render(SAMPLE_RESUME);
    const { Packer } = await import("docx");
    const blob = await Packer.toBlob(document);
    docxCacheRef.current = { ...docxCacheRef.current, [templateId]: blob };
    return blob;
  }, []);

  const handleTabChange = useCallback((value: string) => {
    if (value in resumeTemplateMap) {
      setActiveId(value as ResumeTemplateId);
    }
  }, []);

  const handleDownloadDocx = useCallback(async () => {
    try {
      setDownloadingFormat("docx");
      const blob = await ensureDocxBlob(activeId);
      saveTemplateFile(`hyred-template-cv-ats-${activeId}.docx`, blob, {
        template: activeId,
        format: "docx",
      });
      setHasDownloaded(true);
    } catch (err) {
      console.error("Failed to download template", err);
      setError(copyError);
    } finally {
      setDownloadingFormat(null);
    }
  }, [activeId, ensureDocxBlob, copyError]);

  const activePdfData = pdfDataCache[activeId] ?? null;

  // The preview already holds the rendered PDF bytes, so this is just a save.
  const handleDownloadPdf = useCallback(() => {
    if (!activePdfData) return;

    try {
      setDownloadingFormat("pdf");
      // Copy into an ArrayBuffer-backed view so the bytes satisfy BlobPart.
      const bytes = new Uint8Array(activePdfData);
      const blob = new Blob([bytes], { type: "application/pdf" });
      saveTemplateFile(`hyred-template-cv-ats-${activeId}.pdf`, blob, {
        template: activeId,
        format: "pdf",
      });
      setHasDownloaded(true);
    } catch (err) {
      console.error("Failed to download template pdf", err);
      setError(copyError);
    } finally {
      setDownloadingFormat(null);
    }
  }, [activeId, activePdfData, copyError]);

  return (
    <Card>
      <CardHeader className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:gap-4">
        <div className="w-full sm:flex-1">
          <CardTitle className="text-lg sm:text-xl">{copyTitle}</CardTitle>
          <p className="mt-2 text-sm text-muted-foreground">
            {copyDescription}
          </p>
        </div>
        {showDownloadButton ? (
          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
            <Button
              onClick={handleDownloadDocx}
              disabled={downloadingFormat !== null}
              className="w-full sm:w-auto"
            >
              {downloadingFormat === "docx" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {copyDownloadDocx}
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingFormat !== null || !activePdfData || isBuilding}
              className="w-full sm:w-auto"
            >
              {downloadingFormat === "pdf" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {copyDownloadPdf}
            </Button>
          </div>
        ) : null}
      </CardHeader>
      {hasDownloaded && afterDownload ? (
        <div role="status" className="px-3 sm:px-6">
          {afterDownload}
        </div>
      ) : null}
      <CardContent className="min-w-0 p-3 sm:p-6">
        <Tabs
          value={activeId}
          onValueChange={handleTabChange}
          className="w-full min-w-0"
        >
          {availableTemplates.length > 1 ? (
            <div className="w-full overflow-x-auto pb-2">
              <TabsList className="inline-flex min-w-max flex-nowrap justify-start gap-1.5 sm:gap-2">
                {availableTemplates.map((template) => (
                  <TabsTrigger
                    key={template.id}
                    value={template.id}
                    className="whitespace-nowrap px-3 py-2 text-xs sm:px-4 sm:text-sm"
                  >
                    {TEMPLATE_LABELS[template.id].name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          ) : null}
          {availableTemplates.map((template) => {
            const meta = TEMPLATE_LABELS[template.id];
            const isActive = template.id === activeId;
            return (
              <TabsContent key={template.id} value={template.id} className="pt-6">
                <div className="min-w-0 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{meta.name}</p>
                    <p className="text-sm text-muted-foreground">{meta.description}</p>
                  </div>
                  <div className="relative h-[70vh] min-h-[500px] overflow-hidden rounded-xl border border-border bg-muted/30 sm:min-h-[520px]">
                    {isActive && error && (
                      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-destructive">
                        {error}
                      </div>
                    )}

                    {isActive && isBuilding && !error && (
                      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>{copyLoading}</span>
                        </div>
                      </div>
                    )}

                    {isActive && activePdfData && !isBuilding && !error && (
                      <PdfJsPreview data={activePdfData} />
                    )}
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
}
